/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { authClientsUpdate } from "../funcs/authClientsUpdate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { AuthClient } from "../models/components/authclient.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateAuthClientRequest,
  UpdateAuthClientSecurity,
} from "../models/operations/updateauthclient.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateAuthClientMutationVariables = {
  request: UpdateAuthClientRequest;
  security?: UpdateAuthClientSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateAuthClientMutationData = AuthClient;

export type UpdateAuthClientMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateAuthClient authClients
 *
 * @remarks
 * Rename, enable or disable an auth client. The change applies to the typed auth configuration of every MCP server that offers the client and takes effect only where typed auth client resolution is enforced. It revokes no upstream credentials; elsewhere, including in shadow mode, an MCP server's existing upstream configuration keeps serving. Requires org:admin.
 */
export function useUpdateAuthClientMutation(
  options?: MutationHookOptions<
    UpdateAuthClientMutationData,
    UpdateAuthClientMutationError,
    UpdateAuthClientMutationVariables
  >,
): UseMutationResult<
  UpdateAuthClientMutationData,
  UpdateAuthClientMutationError,
  UpdateAuthClientMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateAuthClientMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateAuthClient(): MutationKey {
  return ["@gram/client", "authClients", "update"];
}

export function buildUpdateAuthClientMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateAuthClientMutationVariables,
  ) => Promise<UpdateAuthClientMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateAuthClient(),
    mutationFn: function updateAuthClientMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateAuthClientMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(authClientsUpdate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
